import { AnalyticsProvider, AnalyticsConfig, AnalyticsUser } from '../types';

type YmFn = ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number };

declare global {
  interface Window {
    ym?: YmFn;
  }
}

const TAG_URL = 'https://mc.yandex.ru/metrika/tag.js';

/**
 * Яндекс.Метрика. Номер счётчика — `REACT_APP_YM_ID`; без него провайдер молчит.
 *
 * - страницы: `hit` на каждую смену роута (счётчик в режиме `defer`, SPA);
 * - события: `reachGoal(<имя из AnalyticsEvents>)`. В отчётах по конверсиям цель
 *   появляется только после того, как в интерфейсе Метрики заведена цель типа
 *   «JavaScript-событие» с тем же идентификатором — см. docs/ANALYTICS.md;
 * - пользователь: только внутренний id и обезличенные признаки. Email и имя в
 *   Метрику не уходят — это запрещено её условиями.
 */
export class YandexMetrikaProvider implements AnalyticsProvider {
  private counterId: number | null = null;
  private isInitialized = false;
  // Дочерние эффекты React срабатывают раньше init() — первый pageView приходит
  // до него. Держим вызовы, пока не станет ясно, есть ли счётчик.
  private pending: unknown[][] = [];
  private lastUrl: string | undefined;

  init(config: AnalyticsConfig): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    const id = Number(process.env.REACT_APP_YM_ID);
    if (!id || process.env.REACT_APP_DISABLE_ANALYTICS === 'true') {
      this.pending = [];
      return;
    }

    // Стандартная заглушка Метрики: вызовы копятся в ym.a, пока грузится tag.js.
    if (!window.ym) {
      const stub: YmFn = (...args: unknown[]) => {
        (stub.a = stub.a || []).push(args);
      };
      stub.l = Date.now();
      window.ym = stub;
      const script = document.createElement('script');
      script.async = true;
      script.src = TAG_URL;
      document.head.appendChild(script);
    }

    this.counterId = id;
    window.ym(id, 'init', {
      defer: true,
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      // Вебвизор включён на время запуска — искать ошибки по записям сессий.
      // В записи попадает всё, что видно на экране. Выключатель:
      // REACT_APP_YM_WEBVISOR=false.
      webvisor: process.env.REACT_APP_YM_WEBVISOR !== 'false',
      params: { app: config.app, environment: config.environment },
    });

    const queued = this.pending;
    this.pending = [];
    queued.forEach((args) => this.call(...args));
  }

  private call(...args: unknown[]): void {
    if (!this.isInitialized) {
      this.pending.push(args);
      return;
    }
    if (this.counterId === null) return;
    window.ym?.(this.counterId, ...args);
  }

  identify(user: AnalyticsUser): void {
    this.call('setUserID', user.id);
    this.call('userParams', {
      UserID: user.id,
      is_admin: Boolean(user.isAdmin),
      profession: user.profession,
      tenant_id: user.tenantId,
    });
  }

  reset(): void {
    // У Метрики нет «разлогина»: следующий identify перезапишет UserID.
  }

  track(event: string, properties?: Record<string, unknown>): void {
    this.call('reachGoal', event, properties);
  }

  pageView(path: string): void {
    const referer = this.lastUrl;
    this.lastUrl = path;
    this.call('hit', path, referer ? { referer } : {});
  }

  isFeatureEnabled(): boolean | undefined {
    return undefined;
  }

  getFeatureFlag(): string | boolean | undefined {
    return undefined;
  }

  onFeatureFlagsLoaded(): void {}
}
